import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, X } from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import { isReadOnly } from '../lib/readOnly';
import { useBoard, useCreateTask, useProjects } from '../lib/projects';
import { Button, FormError, SelectField, TextField } from './ui';
import { t } from '../lib/i18n';

/**
 * Botón flotante (FAB) móvil para crear una tarea rápido desde cualquier pantalla
 * — pensado para quien no tiene la compu cerca. Elegís proyecto y columna, ponés
 * el título y listo. Sólo se muestra en móvil (en desktop está el alta del
 * tablero) y si la empresa puede escribir (oculto en solo-lectura por trial).
 */
export function QuickCreateTask() {
  const { company } = useAuth();
  const projectsQ = useProjects();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Apertura por URL (?nueva=1): la usa la app móvil (isla web "Crear tarea") para
  // saltar directo al alta. Limpiamos el param para no reabrir al cerrar/navegar.
  useEffect(() => {
    if (searchParams.get('nueva') === '1') {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('nueva');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Oculto en solo-lectura (trial vencido) o si todavía no hay proyectos.
  if (isReadOnly(company)) return null;
  const projects = projectsQ.data ?? [];
  if (projects.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('quickTask.button')}
        className="fixed bottom-5 left-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-brand-ink shadow-lift transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg sm:hidden"
      >
        <Plus size={24} />
      </button>
      {open && <QuickCreateDialog projects={projects} onClose={() => setOpen(false)} />}
    </>
  );
}

function QuickCreateDialog({
  projects,
  onClose,
}: {
  projects: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const [columnId, setColumnId] = useState('');
  const [title, setTitle] = useState('');
  const [assignMe, setAssignMe] = useState(true);
  const [error, setError] = useState('');

  const boardQ = useBoard(projectId || undefined);
  const columns = boardQ.data?.columns ?? [];
  const create = useCreateTask(projectId);

  // Al cargar/cambiar el tablero, seleccionar la primera columna por defecto.
  useEffect(() => {
    if (columns.length > 0 && !columns.some((c) => c.id === columnId)) {
      setColumnId(columns[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError(t('quickTask.titleRequired'));
      return;
    }
    if (!columnId) {
      setError(t('quickTask.columnRequired'));
      return;
    }
    setError('');
    try {
      await create.mutateAsync({
        column_id: columnId,
        title: trimmed,
        assignee_id: assignMe ? user?.id ?? null : null,
      });
      onClose();
      navigate(`/proyectos/${projectId}`);
    } catch {
      setError(t('quickTask.error'));
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay-in fixed inset-0 z-50 bg-ink/50 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="modal-in fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-token border border-line bg-surface shadow-lift focus:outline-none"
        >
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <Dialog.Title className="flex items-center gap-2 font-display text-lg font-semibold text-ink">
              <Plus size={18} className="text-brand" />
              {t('quickTask.title')}
            </Dialog.Title>
            <Dialog.Close
              className="grid h-8 w-8 place-items-center rounded-token text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              aria-label={t('common.close')}
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-5">
            {error && <FormError>{error}</FormError>}

            <SelectField
              name="qt_project"
              label={t('quickTask.project')}
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setColumnId('');
              }}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>

            <SelectField
              name="qt_column"
              label={t('quickTask.column')}
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              disabled={boardQ.isLoading || columns.length === 0}
            >
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </SelectField>

            <TextField
              name="qt_title"
              label={t('quickTask.titleLabel')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={assignMe}
                onChange={(e) => setAssignMe(e.target.checked)}
                className="h-4 w-4 rounded border-line accent-[color:var(--brand)]"
              />
              {t('quickTask.assignMe')}
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={() => void submit()} loading={create.isPending} disabled={!title.trim()}>
              {t('quickTask.create')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
